import { useState } from 'react';

// Import components
import VideoPlayer from '../video-player/VideoPlayer';
import VideoModal from '../video-player/VideoModal';

import { MEDIA } from '../../constants/media';

type FeatureVideos = {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  videoSrc: string;
};

const FEATURE_VIDEOS: FeatureVideos[] = [
  {
    id: 'auto-availability',
    title: 'Explore WhenWeMeet',
    subtitle: 'Auto availability overview',
    description:
      'See how WhenWeMeet brings everyone’s availability into a single, interactive view so you can instantly spot the perfect time.',
    videoSrc: MEDIA.preview1,
  },
  {
    id: 'participation-flow',
    title: 'Participation – simple for everyone',
    subtitle: 'Invite, respond, and confirm in seconds',
    description:
      'Watch how participants join with a link, mark when they are free, and help you find the best overlap without any accounts.',
    videoSrc: MEDIA.preview2,
  },
  {
    id: 'confirm-time',
    title: 'Lock in the final time',
    subtitle: 'Pick the winning slot and add to calendars',
    description:
      'See how easy it is to choose the best slot, share the decision with your group, and add it straight to your calendar.',
    videoSrc: MEDIA.preview3,
  },
];

export default function FeatureVideos() {
  const [activeVideo, setActiveVideo] = useState<FeatureVideos | null>(null);

  const handleOpen = (feature: FeatureVideos) => {
    setActiveVideo(feature);
  };

  const handleClose = () => {
    setActiveVideo(null);
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 px-4 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-semibold text-center mb-2">
          Explore WhenWeMeet in action
        </h2>
        <p className="text-gray-600 text-sm sm:text-base text-center max-w-2xl mx-auto mb-8">
          Short previews that show how meeting planners are created, how guests
          respond, and how you confirm the final time.
        </p>

        <div className="space-y-10 sm:space-y-12">
          {FEATURE_VIDEOS.map((feature, index) => (
            <div
              key={feature.id}
              className={`flex flex-col items-center gap-6 sm:gap-10 md:gap-12 ${
                index % 2 === 1 ? 'md:flex-row-reverse' : 'md:flex-row'
              }`}>
              {/* Autoplay video preview */}
              <VideoPlayer
                src={feature.videoSrc}
                onClick={() => handleOpen(feature)}
                className="w-full md:w-1/2 max-w-xl"
              />

              {/* Text content */}
              <div className="w-full md:w-1/2 max-w-lg text-center md:text-left">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
                  {feature.title}
                </h3>
                <p className="text-base font-medium text-gray-800 mb-3">
                  {feature.subtitle}
                </p>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Video popup */}
      <VideoModal
        isOpen={!!activeVideo}
        src={activeVideo?.videoSrc || ''}
        onClose={handleClose}
      />
    </section>
  );
}
